import { apiEmployees } from "@/api/employees/employee.api.ts";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import { PreviewEmployeesModalColumn } from "@/pages/employees/employee/widgets/preview-employees-modal.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useEmployeesInfinite } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiEmployee, ApiEmployeeFilter } from "@/types/employees/employee.types.ts";
import {
  Box,
  Button,
  Modal,
  ModalProps,
  Search,
  Skeleton,
  Stack,
  Table,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDebounce, useEffectOnce } from "react-use";

export interface PreviewEmployeesModalProps extends ModalProps {
  filter: ApiEmployeeFilter;
  addColumns: PreviewEmployeesModalColumn[];
  modalTitle?: string;
  source?: string;
}

export const PreviewEmployeesModal = ({
  filter,
  addColumns,
  modalTitle,
  source,
  ...props
}: PreviewEmployeesModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { downloadFile } = useDownloadHelper();

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useDebounce(
    () => {
      setSearchTerm(searchInput);
    },
    300,
    [searchInput]
  );

  //https://www.mantine-react-table.com/docs/examples/infinite-scrolling
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { renderEmployeeName, renderPartialDate } = useFormatter();

  const {
    data: employees,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useEmployeesInfinite({
    ...filter,
    text: searchTerm || undefined,
  });

  const flatData = useMemo(
    () => employees?.pages?.flatMap((page) => page?.items ?? []) ?? [],
    [employees]
  );

  const fetchMoreOnBottomReached = useCallback(
    (scrollHeight: number, scrollTop: number, clientHeight: number) => {
      if (
        scrollHeight - scrollTop - clientHeight < 400 &&
        !isFetching &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, isFetching, hasNextPage, isFetchingNextPage]
  );

  const columnHelper = createColumnHelper<ApiEmployee>();
  const columns = useMemo<ColumnDef<ApiEmployee>[]>(() => {
    const inner: DisplayColumnDef<ApiEmployee, unknown>[] = [
      columnHelper.display({
        header: t("core:employee"),
        cell: (row) => (
          <Link to={`/employees/${row.row.original.id}/`} target={"_blank"}>
            <Stack direction={"horizontal"} gap={"md"} items={"center"}>
              <EmployeeAvatar employee={row.row.original} size={"md"} />
              <Text className={"text-title"}>{renderEmployeeName(row.row.original)}</Text>
            </Stack>
          </Link>
        ),
        ...ColumnSizes.employee,
      }),
      columnHelper.display({
        header: t("core:position"),
        cell: (cell) => (
          <Text className={"text-default"}>{getEmployeePositionTitle(cell.row.original)}</Text>
        ),
        ...ColumnSizes.standard,
      }),
      columnHelper.display({
        header: t("core:department"),
        cell: (cell) => (
          <Text className={"text-default"}>
            {cell.row.original.employee_position?.department?.title}
          </Text>
        ),
        ...ColumnSizes.standard,
      }),
    ];

    if (addColumns.includes(PreviewEmployeesModalColumn.birth_date)) {
      inner.push(
        columnHelper.display({
          header: t("core:birth_date"),
          cell: (cell) => (
            <Text className={"text-default"}>
              {cell.row.original.birth_date ? renderPartialDate(cell.row.original.birth_date) : "—"}
            </Text>
          ),
        })
      );
    }

    return inner;
  }, [flatData]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.employees.previewViewed, {
      [analyticProperties.source]: source,
    });
  });

  const handleExportClick = useCallback(() => {
    const filterToExport = {
      ...filter,
      text: searchTerm || undefined,
      format: "xlsx",
    };

    downloadFile({
      apiCall: () => apiEmployees.export(filterToExport),
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.dataExported, {
          [analyticProperties.title]: "Employees",
        });
      },
    });
  }, [filter, searchTerm]);

  return (
    <Modal
      {...props}
      layout="stretch"
      size={"xl"}
      title={
        modalTitle ||
        (employees && employees.pages.length > 0
          ? `${t("plurals:employees_count", { count: employees.pages[0]?.metadata.total_items })}`
          : "")
      }
      withCloser
      closeByEscEnabled
      onModalContentScroll={(scrollTop, scrollHeight, height) => {
        fetchMoreOnBottomReached(scrollHeight, scrollTop, height);
      }}
      headerActionsSlot={
        <Button variant={"secondary"} onClick={handleExportClick} size={"sm"}>
          {t("common:export_to_xlsx")}
        </Button>
      }
      actions={
        <Stack gap={"sm"}>
          <Button onClick={() => close(id)} size={"lg"} className={"w-full"}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <Box className={"relative"}>
        <Stack className={"overflow-hidden"} gap={"md"}>
          <Search
            value={searchInput}
            onInput={(e) => setSearchInput(e.currentTarget.value)}
            placeholder={t("common:search")}
            clearable={true}
            onClear={() => setSearchInput("")}
          />
          <Box ref={tableContainerRef} className={"overflow-y-auto h-full"}>
            <Table data={flatData} columns={columns} showSkeleton={isLoading} />
            {!isLoading && isFetching && <Skeleton className={"h-10 w-full"} />}
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};
